// Single source of truth for all plan configuration, feature flags, upload limits, and pricing.
// Every feature gate in the app should derive from this file.

// ─── Tier type ───────────────────────────────────────────────────────────────
// These strings match exactly what is stored in user_profiles.subscription_tier.
export type PlanTier =
  | 'free'
  | 'collective_free'
  | 'collective_verified'
  | 'collective_pro'
  | 'verified'
  | 'legacy'
  | 'institution_starter'
  | 'institution_growth'
  | 'vaas';

// Legacy alias — some older code stored 'pro' as an alias for 'verified'.
// Normalize it with normalizeTier() before any feature check.
export type PlanTierRaw = PlanTier | 'pro' | 'individual_verified' | 'individual_legacy';

export type CircleRoomBackgroundId =
  | 'archive_room'
  | 'library_room'
  | 'memory_room'
  | 'garden_pavilion'
  | 'glass_room';

export const CIRCLE_ROOM_BACKGROUND_IDS: CircleRoomBackgroundId[] = [
  'archive_room',
  'library_room',
  'memory_room',
  'garden_pavilion',
  'glass_room',
];

export type CirclePlanLimits = {
  maxCircles: number;
  maxMembersPerCircle: number;
  maxTables: 1 | 2;
  canCustomizeBackground: boolean;
  canUsePremiumBackgrounds: boolean;
};

const FREE_CIRCLE_LIMITS: CirclePlanLimits = {
  maxCircles: 1,
  maxMembersPerCircle: 3,
  maxTables: 1,
  canCustomizeBackground: false,
  canUsePremiumBackgrounds: false,
};

/** Product limits for Circles. A maxCircles value of -1 means unlimited. */
export function getCirclePlanLimits(tier: PlanTier): CirclePlanLimits {
  if (tier === 'legacy') {
    return {
      maxCircles: -1,
      maxMembersPerCircle: 24,
      maxTables: 2,
      canCustomizeBackground: true,
      canUsePremiumBackgrounds: true,
    };
  }
  if (
    tier === 'verified' ||
    tier === 'collective_verified' ||
    tier === 'collective_pro' ||
    tier === 'vaas' ||
    tier === 'institution_starter' ||
    tier === 'institution_growth'
  ) {
    return {
      maxCircles: 5,
      maxMembersPerCircle: 8,
      maxTables: 1,
      canCustomizeBackground: true,
      canUsePremiumBackgrounds: false,
    };
  }
  return FREE_CIRCLE_LIMITS;
}

export function canUseCircleRoomBackground(
  tier: PlanTier,
  backgroundId: CircleRoomBackgroundId
): boolean {
  if (backgroundId === 'archive_room') return true;
  const limits = getCirclePlanLimits(tier);
  if (!limits.canCustomizeBackground) return false;
  if (backgroundId === 'garden_pavilion') return limits.canUsePremiumBackgrounds;
  return true;
}

export function normalizeTier(raw: string | null | undefined): PlanTier {
  switch (raw) {
    case 'pro':
    case 'individual_verified':
      return 'verified';
    case 'individual_legacy':
      return 'legacy';
    case 'collective_free':
    case 'collective_verified':
    case 'collective_pro':
    case 'verified':
    case 'legacy':
    case 'institution_starter':
    case 'institution_growth':
    case 'vaas':
      return raw as PlanTier;
    default:
      return 'free';
  }
}

// ─── Content types ───────────────────────────────────────────────────────────
export type StoryType =
  | 'written'
  | 'voice_note'
  | 'photo'
  | 'video'
  | 'podcast'
  | 'document'
  | 'external_link';

export type UploadFileType = 'image' | 'audio' | 'video' | 'document';

// ─── Per-plan feature config ─────────────────────────────────────────────────
export interface PlanConfig {
  tier: PlanTier;
  displayName: string;
  monthlyPrice: number; // USD cents (0 = free)
  yearlyPrice: number; // USD cents (0 = free or N/A)
  monthlyPriceDisplay: string;
  yearlyPriceDisplay: string;
  stripePriceIdMonthly: string | null;
  stripePriceIdYearly: string | null;

  // Storage
  storageLimitBytes: number;
  maxImageBytes: number;
  maxAudioBytes: number;
  maxVideoBytes: number;
  maxDocumentBytes: number;

  // Story / content types allowed
  allowedStoryTypes: StoryType[];

  // Upload type flags
  canUploadPhotos: boolean;
  canUploadVoice: boolean;
  canUploadVideo: boolean;
  canUploadDocuments: boolean;
  canUploadPodcasts: boolean;
  canCreateExternalLinks: boolean;

  // Feature flags — individual
  canSubmitAchievements: boolean;
  maxAchievements: number; // -1 = unlimited
  canUploadCredentials: boolean;
  canRequestVerification: boolean;
  canGenerateProofCards: boolean;
  canUseJourneyMap: boolean;
  canUsePublicVault: boolean;
  canAccessAnalytics: boolean;
  canAccessActivityLog: boolean;
  canShareLinks: boolean;
  featuredBoosts: number; // story boosts per month
  verifiedBadge: boolean;
  allPillarsVerified: boolean;

  // Feature flags — individual gated
  canUseFamilyLedger: boolean;
  canUseTimeCapsule: boolean;
  canUseAdvancedAnalytics: boolean; // Legacy only: full country + device breakdown

  // Feature flags — institution
  canUseInstitutionDashboard: boolean;
  canUseStoryLedger: boolean;
  canUseVerificationInbox: boolean;
  canUseHeritageArchive: boolean;
  canUseGeospatial: boolean;
  canUseBulkVerification: boolean;
  canUseAPI: boolean;
  maxVerificationsPerMonth: number; // -1 = unlimited
  maxSeats: number; // -1 = unlimited
  maxRecords: number; // -1 = unlimited

  // Monthly upload cap (total files, across all types). -1 = unlimited
  maxMonthlyUploads: number;

  supportLevel: 'community' | 'email' | 'priority' | 'dedicated';
}

// ─── Stripe price IDs ─────────────────────────────────────────────────────────
// These are read from env vars at runtime. The fallbacks are the production IDs
// from the sync-catalog run. Only used where NEXT_PUBLIC_* vars are needed.
const MB = 1024 * 1024;
const GB = 1024 * MB;

// ─── Plan definitions ─────────────────────────────────────────────────────────
const ALL_STORY_TYPES: StoryType[] = [
  'written',
  'voice_note',
  'photo',
  'video',
  'podcast',
  'document',
  'external_link',
];

function collectivePlanConfig(
  tier: 'collective_free' | 'collective_verified' | 'collective_pro'
): PlanConfig {
  const verified = tier !== 'collective_free';
  const pro = tier === 'collective_pro';
  return {
    tier,
    displayName:
      tier === 'collective_free'
        ? 'Collective Free'
        : tier === 'collective_verified'
          ? 'Collective Verified'
          : 'Collective Pro',
    monthlyPrice: tier === 'collective_verified' ? 2900 : pro ? 7900 : 0,
    yearlyPrice: tier === 'collective_verified' ? 27840 : pro ? 75840 : 0,
    monthlyPriceDisplay: tier === 'collective_verified' ? '$29' : pro ? '$79' : '$0',
    yearlyPriceDisplay: tier === 'collective_verified' ? '$278.40' : pro ? '$758.40' : '$0',
    stripePriceIdMonthly: null,
    stripePriceIdYearly: null,
    storageLimitBytes: pro ? 1024 * GB : verified ? 100 * GB : 5 * GB,
    maxImageBytes: verified ? 20 * MB : 10 * MB,
    maxAudioBytes: verified ? 100 * MB : 50 * MB,
    maxVideoBytes: pro ? 500 * MB : verified ? 100 * MB : 0,
    maxDocumentBytes: pro ? 500 * MB : verified ? 100 * MB : 25 * MB,
    allowedStoryTypes: verified ? ALL_STORY_TYPES : ['written', 'photo', 'external_link'],
    canUploadPhotos: true,
    canUploadVoice: verified,
    canUploadVideo: verified,
    canUploadDocuments: verified,
    canUploadPodcasts: verified,
    canCreateExternalLinks: true,
    canSubmitAchievements: true,
    maxAchievements: -1,
    canUploadCredentials: verified,
    canRequestVerification: verified,
    canGenerateProofCards: verified,
    canUseJourneyMap: false,
    canUsePublicVault: true,
    canAccessAnalytics: verified,
    canAccessActivityLog: verified,
    canShareLinks: true,
    featuredBoosts: pro ? 10 : verified ? 3 : 0,
    verifiedBadge: verified,
    allPillarsVerified: false,
    canUseFamilyLedger: false,
    canUseTimeCapsule: false,
    canUseAdvancedAnalytics: pro,
    canUseInstitutionDashboard: true,
    canUseStoryLedger: true,
    canUseVerificationInbox: verified,
    canUseHeritageArchive: verified,
    canUseGeospatial: pro,
    canUseBulkVerification: pro,
    canUseAPI: pro,
    maxVerificationsPerMonth: pro ? 500 : verified ? 50 : 0,
    maxSeats: pro ? 25 : verified ? 5 : 1,
    maxRecords: pro ? -1 : verified ? 1000 : 100,
    maxMonthlyUploads: pro ? -1 : verified ? 500 : 25,
    supportLevel: pro ? 'priority' : verified ? 'email' : 'community',
  };
}

export const PLAN_CONFIG: Record<PlanTier, PlanConfig> = {
  free: {
    tier: 'free',
    displayName: 'Individual — Free',
    monthlyPrice: 0,
    yearlyPrice: 0,
    monthlyPriceDisplay: '$0',
    yearlyPriceDisplay: '$0',
    stripePriceIdMonthly: null,
    stripePriceIdYearly: null,

    storageLimitBytes: 100 * MB,
    maxImageBytes: 5 * MB,
    maxAudioBytes: 0,
    maxVideoBytes: 50 * MB,
    maxDocumentBytes: 0,

    allowedStoryTypes: ['written', 'photo', 'video', 'external_link'],

    canUploadPhotos: true,
    canUploadVoice: false,
    canUploadVideo: true,
    canUploadDocuments: false,
    canUploadPodcasts: false,
    canCreateExternalLinks: true,

    canSubmitAchievements: true,
    maxAchievements: 3,
    canUploadCredentials: false,
    canRequestVerification: false,
    canGenerateProofCards: false,
    canUseJourneyMap: false,
    canUsePublicVault: true,
    canAccessAnalytics: false,
    canAccessActivityLog: false,
    canShareLinks: true,
    featuredBoosts: 0,
    verifiedBadge: false,
    allPillarsVerified: false,
    canUseFamilyLedger: false,
    canUseTimeCapsule: false,
    canUseAdvancedAnalytics: false,

    canUseInstitutionDashboard: false,
    canUseStoryLedger: false,
    canUseVerificationInbox: false,
    canUseHeritageArchive: false,
    canUseGeospatial: false,
    canUseBulkVerification: false,
    canUseAPI: false,
    maxVerificationsPerMonth: 0,
    maxSeats: 1,
    maxRecords: 3,

    maxMonthlyUploads: 10,
    supportLevel: 'community',
  },

  collective_free: collectivePlanConfig('collective_free'),
  collective_verified: collectivePlanConfig('collective_verified'),
  collective_pro: collectivePlanConfig('collective_pro'),

  verified: {
    tier: 'verified',
    displayName: 'Individual — Verified',
    monthlyPrice: 999,
    yearlyPrice: 9590,
    monthlyPriceDisplay: '$9.99',
    yearlyPriceDisplay: '$95.90',
    stripePriceIdMonthly: null,
    stripePriceIdYearly: null,

    storageLimitBytes: 5 * GB,
    maxImageBytes: 10 * MB,
    maxAudioBytes: 50 * MB,
    maxVideoBytes: 0,
    maxDocumentBytes: 25 * MB,

    allowedStoryTypes: ['written', 'photo', 'voice_note', 'podcast', 'document', 'external_link'],

    canUploadPhotos: true,
    canUploadVoice: true,
    canUploadVideo: false,
    canUploadDocuments: true,
    canUploadPodcasts: true,
    canCreateExternalLinks: true,

    canSubmitAchievements: true,
    maxAchievements: -1,
    canUploadCredentials: true,
    canRequestVerification: true,
    canGenerateProofCards: true,
    canUseJourneyMap: true,
    canUsePublicVault: true,
    canAccessAnalytics: true,
    canAccessActivityLog: true,
    canShareLinks: true,
    featuredBoosts: 1,
    verifiedBadge: true,
    allPillarsVerified: false,
    canUseFamilyLedger: false,
    canUseTimeCapsule: true,
    canUseAdvancedAnalytics: false,

    canUseInstitutionDashboard: false,
    canUseStoryLedger: false,
    canUseVerificationInbox: false,
    canUseHeritageArchive: false,
    canUseGeospatial: false,
    canUseBulkVerification: false,
    canUseAPI: false,
    maxVerificationsPerMonth: 0,
    maxSeats: 1,
    maxRecords: -1,

    maxMonthlyUploads: -1,
    supportLevel: 'email',
  },

  legacy: {
    tier: 'legacy',
    displayName: 'Individual — Legacy',
    monthlyPrice: 1699,
    yearlyPrice: 16310,
    monthlyPriceDisplay: '$16.99',
    yearlyPriceDisplay: '$163.10',
    stripePriceIdMonthly: null,
    stripePriceIdYearly: null,

    storageLimitBytes: 20 * GB,
    maxImageBytes: 20 * MB,
    maxAudioBytes: 100 * MB,
    maxVideoBytes: 500 * MB,
    maxDocumentBytes: 50 * MB,

    allowedStoryTypes: ALL_STORY_TYPES,

    canUploadPhotos: true,
    canUploadVoice: true,
    canUploadVideo: true,
    canUploadDocuments: true,
    canUploadPodcasts: true,
    canCreateExternalLinks: true,

    canSubmitAchievements: true,
    maxAchievements: -1,
    canUploadCredentials: true,
    canRequestVerification: true,
    canGenerateProofCards: true,
    canUseJourneyMap: true,
    canUsePublicVault: true,
    canAccessAnalytics: true,
    canAccessActivityLog: true,
    canShareLinks: true,
    featuredBoosts: 3,
    verifiedBadge: true,
    allPillarsVerified: true,
    canUseFamilyLedger: true,
    canUseTimeCapsule: true,
    canUseAdvancedAnalytics: true,

    canUseInstitutionDashboard: false,
    canUseStoryLedger: false,
    canUseVerificationInbox: false,
    canUseHeritageArchive: false,
    canUseGeospatial: true,
    canUseBulkVerification: false,
    canUseAPI: false,
    maxVerificationsPerMonth: 0,
    maxSeats: 1,
    maxRecords: -1,

    maxMonthlyUploads: -1,
    supportLevel: 'priority',
  },

  institution_starter: {
    tier: 'institution_starter',
    displayName: 'Institution — Starter',
    monthlyPrice: 9900,
    yearlyPrice: 95000,
    monthlyPriceDisplay: '$99',
    yearlyPriceDisplay: '$950',
    stripePriceIdMonthly: null,
    stripePriceIdYearly: null,

    storageLimitBytes: 100 * GB,
    maxImageBytes: 20 * MB,
    maxAudioBytes: 100 * MB,
    maxVideoBytes: 500 * MB,
    maxDocumentBytes: 100 * MB,

    allowedStoryTypes: ALL_STORY_TYPES,

    canUploadPhotos: true,
    canUploadVoice: true,
    canUploadVideo: true,
    canUploadDocuments: true,
    canUploadPodcasts: true,
    canCreateExternalLinks: true,

    canSubmitAchievements: true,
    maxAchievements: -1,
    canUploadCredentials: true,
    canRequestVerification: true,
    canGenerateProofCards: true,
    canUseJourneyMap: true,
    canUsePublicVault: true,
    canAccessAnalytics: true,
    canAccessActivityLog: true,
    canShareLinks: true,
    featuredBoosts: 0,
    verifiedBadge: false,
    allPillarsVerified: false,
    canUseFamilyLedger: false,
    canUseTimeCapsule: false,
    canUseAdvancedAnalytics: true,

    canUseInstitutionDashboard: true,
    canUseStoryLedger: true,
    canUseVerificationInbox: true,
    canUseHeritageArchive: true,
    canUseGeospatial: true,
    canUseBulkVerification: false,
    canUseAPI: false,
    maxVerificationsPerMonth: 500,
    maxSeats: 5,
    maxRecords: 1000,

    maxMonthlyUploads: -1,
    supportLevel: 'email',
  },

  institution_growth: {
    tier: 'institution_growth',
    displayName: 'Institution — Growth',
    monthlyPrice: 29900,
    yearlyPrice: 287000,
    monthlyPriceDisplay: '$299',
    yearlyPriceDisplay: '$2,870',
    stripePriceIdMonthly: null,
    stripePriceIdYearly: null,

    storageLimitBytes: 500 * GB,
    maxImageBytes: 20 * MB,
    maxAudioBytes: 100 * MB,
    maxVideoBytes: 500 * MB,
    maxDocumentBytes: 500 * MB,

    allowedStoryTypes: ALL_STORY_TYPES,

    canUploadPhotos: true,
    canUploadVoice: true,
    canUploadVideo: true,
    canUploadDocuments: true,
    canUploadPodcasts: true,
    canCreateExternalLinks: true,

    canSubmitAchievements: true,
    maxAchievements: -1,
    canUploadCredentials: true,
    canRequestVerification: true,
    canGenerateProofCards: true,
    canUseJourneyMap: true,
    canUsePublicVault: true,
    canAccessAnalytics: true,
    canAccessActivityLog: true,
    canShareLinks: true,
    featuredBoosts: 0,
    verifiedBadge: false,
    allPillarsVerified: false,
    canUseFamilyLedger: false,
    canUseTimeCapsule: false,
    canUseAdvancedAnalytics: true,

    canUseInstitutionDashboard: true,
    canUseStoryLedger: true,
    canUseVerificationInbox: true,
    canUseHeritageArchive: true,
    canUseGeospatial: true,
    canUseBulkVerification: true,
    canUseAPI: true,
    maxVerificationsPerMonth: -1,
    maxSeats: -1,
    maxRecords: -1,

    maxMonthlyUploads: -1,
    supportLevel: 'dedicated',
  },

  vaas: {
    tier: 'vaas',
    displayName: 'Verification (one-time)',
    monthlyPrice: 2900,
    yearlyPrice: 0,
    monthlyPriceDisplay: '$29',
    yearlyPriceDisplay: '',
    stripePriceIdMonthly: null,
    stripePriceIdYearly: null,

    // VaaS is a one-time verification purchase; storage/upload limits match free.
    storageLimitBytes: 500 * MB,
    maxImageBytes: 5 * MB,
    maxAudioBytes: 0,
    maxVideoBytes: 0,
    maxDocumentBytes: 0,

    allowedStoryTypes: ['written', 'photo', 'external_link'],

    canUploadPhotos: true,
    canUploadVoice: false,
    canUploadVideo: false,
    canUploadDocuments: false,
    canUploadPodcasts: false,
    canCreateExternalLinks: true,

    canSubmitAchievements: true,
    maxAchievements: 3,
    canUploadCredentials: false,
    canRequestVerification: true,
    canGenerateProofCards: true,
    canUseJourneyMap: true,
    canUsePublicVault: true,
    canAccessAnalytics: false,
    canAccessActivityLog: false,
    canShareLinks: true,
    featuredBoosts: 0,
    verifiedBadge: false,
    allPillarsVerified: false,
    canUseFamilyLedger: false,
    canUseTimeCapsule: false,
    canUseAdvancedAnalytics: false,

    canUseInstitutionDashboard: false,
    canUseStoryLedger: false,
    canUseVerificationInbox: false,
    canUseHeritageArchive: false,
    canUseGeospatial: false,
    canUseBulkVerification: false,
    canUseAPI: false,
    maxVerificationsPerMonth: 1,
    maxSeats: 1,
    maxRecords: 3,

    maxMonthlyUploads: 10,
    supportLevel: 'community',
  },
};

// ─── Helper functions ────────────────────────────────────────────────────────

export function getPlanConfig(tier: PlanTier): PlanConfig {
  return PLAN_CONFIG[tier] ?? PLAN_CONFIG.free;
}

export function getPlanLimits(tier: PlanTier): PlanConfig {
  return getPlanConfig(tier);
}

export function canCreateStoryType(plan: PlanTier, storyType: StoryType): boolean {
  return getPlanConfig(plan).allowedStoryTypes.includes(storyType);
}

export function canUploadFileType(plan: PlanTier, fileType: UploadFileType): boolean {
  const config = getPlanConfig(plan);
  switch (fileType) {
    case 'image':
      return config.canUploadPhotos;
    case 'audio':
      return config.canUploadVoice || config.canUploadPodcasts;
    case 'video':
      return config.canUploadVideo;
    case 'document':
      return config.canUploadDocuments;
  }
}

export function canSubmitAchievement(plan: PlanTier, currentCount: number): boolean {
  const config = getPlanConfig(plan);
  if (!config.canSubmitAchievements) return false;
  if (config.maxRecords === -1) return true;
  return currentCount < config.maxRecords;
}

export function canUploadCredential(plan: PlanTier): boolean {
  return getPlanConfig(plan).canUploadCredentials;
}

export function canUseInstitutionFeature(
  plan: PlanTier,
  feature: keyof Pick<
    PlanConfig,
    | 'canUseInstitutionDashboard'
    | 'canUseStoryLedger'
    | 'canUseVerificationInbox'
    | 'canUseHeritageArchive'
    | 'canUseGeospatial'
    | 'canUseBulkVerification'
    | 'canUseAPI'
  >
): boolean {
  return Boolean(getPlanConfig(plan)[feature]);
}

export function getStorageLimit(plan: PlanTier): number {
  return getPlanConfig(plan).storageLimitBytes;
}

/** Human-readable vault quota for UI (matches upload enforcement). */
export function mediaVaultStorageLabel(plan: PlanTier): string {
  return formatBytes(getPlanConfig(plan).storageLimitBytes);
}

export function getRemainingStorage(plan: PlanTier, usedBytes: number): number {
  return Math.max(0, getStorageLimit(plan) - usedBytes);
}

// ─── Upload file validation ───────────────────────────────────────────────────

export interface FileValidationResult {
  ok: boolean;
  error?: string;
  category?: UploadFileType;
}

const ALLOWED_IMAGE_MIMES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/heic',
  'image/heif',
];
const ALLOWED_AUDIO_MIMES = [
  'audio/mpeg',
  'audio/mp4',
  'audio/webm',
  'audio/ogg',
  'audio/wav',
  'audio/aac',
  'audio/x-m4a',
];
const ALLOWED_VIDEO_MIMES = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo'];
const ALLOWED_DOCUMENT_MIMES = [
  'application/pdf',
  'text/plain',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

export function getMimeCategory(mimeType: string): UploadFileType | 'unknown' {
  const m = mimeType.toLowerCase();
  if (ALLOWED_IMAGE_MIMES.includes(m)) return 'image';
  if (ALLOWED_AUDIO_MIMES.includes(m)) return 'audio';
  if (ALLOWED_VIDEO_MIMES.includes(m)) return 'video';
  if (ALLOWED_DOCUMENT_MIMES.includes(m)) return 'document';
  return 'unknown';
}

export function formatBytes(bytes: number): string {
  if (bytes >= GB) return `${(bytes / GB).toFixed(1)} GB`;
  if (bytes >= MB) return `${Math.round(bytes / MB)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
}

export function validateFileForUpload(
  file: File,
  plan: PlanTier,
  storageUsedBytes: number
): FileValidationResult {
  const config = getPlanConfig(plan);
  const category = getMimeCategory(file.type);

  if (category === 'unknown') {
    return { ok: false, error: `File type "${file.type}" is not supported.` };
  }

  if (!canUploadFileType(plan, category)) {
    return {
      ok: false,
      error: uploadBlockedMessage(category, plan),
    };
  }

  let maxBytes = 0;
  if (category === 'image') maxBytes = config.maxImageBytes;
  else if (category === 'audio') maxBytes = config.maxAudioBytes;
  else if (category === 'video') maxBytes = config.maxVideoBytes;
  else if (category === 'document') maxBytes = config.maxDocumentBytes;

  if (maxBytes > 0 && file.size > maxBytes) {
    return {
      ok: false,
      error: `This file is ${formatBytes(file.size)}, which exceeds the ${formatBytes(maxBytes)} ${category} limit on the ${config.displayName} plan.`,
    };
  }

  const remaining = getRemainingStorage(plan, storageUsedBytes);
  if (file.size > remaining) {
    return {
      ok: false,
      error: `You have used ${formatBytes(storageUsedBytes)} of your ${formatBytes(config.storageLimitBytes)} storage. This file requires ${formatBytes(file.size)} more space.`,
    };
  }

  return { ok: true, category };
}

// ─── Upgrade messages ─────────────────────────────────────────────────────────

export function uploadBlockedMessage(fileType: UploadFileType | string, plan: PlanTier): string {
  const messages: Record<string, string> = {
    video: 'Video uploads are available on the Legacy and Institution plans.',
    audio: 'Voice and audio uploads are available on the Verified plan and above.',
    document: 'Document uploads are available on the Verified plan and above.',
  };
  return (
    messages[fileType] ||
    `This upload type is not available on the ${getPlanConfig(plan).displayName} plan. Upgrade to unlock it.`
  );
}

export function planUpgradeMessage(storyType: StoryType): string {
  const messages: Record<StoryType, string> = {
    written: '',
    external_link: '',
    photo: 'Upgrade to add photos to your story.',
    voice_note: 'Upgrade to preserve voice notes.',
    podcast: 'Upgrade to preserve podcast episodes.',
    document: 'Upgrade to attach documents.',
    video: 'Upgrade to add video to your story.',
  };
  return messages[storyType] || 'Upgrade to unlock this story type.';
}

export function achievementLimitMessage(plan: PlanTier): string {
  const config = getPlanConfig(plan);
  if (config.maxRecords !== -1) {
    return `You've reached the Free plan limit of ${config.maxRecords} records. Upgrade to Verified to preserve unlimited records.`;
  }
  return '';
}

export function credentialUploadMessage(plan: PlanTier): string {
  if (!getPlanConfig(plan).canUploadCredentials) {
    return 'Credential evidence uploads are available on the Verified plan and above.';
  }
  return '';
}

// ─── Mapping from storyType to file category ─────────────────────────────────
export function storyTypeToFileType(storyType: StoryType): UploadFileType | null {
  switch (storyType) {
    case 'photo':
      return 'image';
    case 'voice_note':
    case 'podcast':
      return 'audio';
    case 'video':
      return 'video';
    case 'document':
      return 'document';
    default:
      return null;
  }
}
